import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';

import { Modal } from 'angular2-modal/plugins/bootstrap';

import { AuthHttpClient } from '../../../providers/auth-http.service';
import { AuthService } from '../../../providers/auth.service';
import { TimeService } from '../../../shared/services/time.service';

import { SharedClientService } from '../shared/services/shared-client.service';
import { SharedThirdPartyIntegrationService } from '../shared/services/shared-third-party-integration.service';

import { showKomboConnect } from '@kombo-api/connect';

import { IApp } from '../../../app';

import {
	CLIENT_THIRD_PARTY_INTEGRATION_API_KEY_TYPE,
	CLIENT_THIRD_PARTY_INTEGRATION_BASIC_TYPE,
	CLIENT_THIRD_PARTY_INTEGRATION_KOMBO_TYPE,
	CLIENT_THIRD_PARTY_INTEGRATION_OAUTH_2_0_TYPE
} from '../../shared/constants/global';
import { default as swal } from 'sweetalert2';

declare var $: any;
declare var window: any;
declare var screen: any;


export class SettingsComponent implements IApp, OnInit, OnDestroy {
	public client: any;
	public accountSettings: any;
	public atsIntegrations: any[];
	public conferencesAndCalendarsIntegrations: any[];
	public securitySettings: any = {
		password: '',
		confirm_password: ''
	};
	public showConfirmYourPasswordInput: boolean = false;
	public mfaPasswordConfirmation: string = '';
	public mfaPasswordConfirmationErrorMessage: string = '';
	public state: any = {
		account: {
			loading: false,
			done: false
		},
		security: {
			loading: false,
			done: false
		},
		mfa: {
			loading: false,
			done: false
		},
		atsCandidateExport: {
			loading: false,
			done: false
		}
	};
	public refreshWebhooksStatusIntervalIdx: any;
	public clientOptions: any[];
	public easyScheduleSettingsPage = `${process.env.CLIENT_APP_URL}/integrations/easy-schedule/settings`;

	public thirdPartyIntegrationsAuthType: any = {
		'oauth20': CLIENT_THIRD_PARTY_INTEGRATION_OAUTH_2_0_TYPE,
		'apikey': CLIENT_THIRD_PARTY_INTEGRATION_API_KEY_TYPE,
		'basic': CLIENT_THIRD_PARTY_INTEGRATION_BASIC_TYPE,
		'kombo': CLIENT_THIRD_PARTY_INTEGRATION_KOMBO_TYPE
	};

	public timezones: any = this.timeService.timezones;

	constructor(
		public modal: Modal,
		public authService: AuthService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private titleService: Title,
		private authHttp: AuthHttpClient,
		private sharedClientService: SharedClientService,
		private sharedThirdPartyIntegrationService: SharedThirdPartyIntegrationService,
		private timeService: TimeService) {
			this.setAppTitle();
	}

	// Lifecycle Events
	public ngOnInit(): void {
		this.activatedRoute.queryParams.subscribe(
			(params: Params) => {
				if (typeof params.error !== 'undefined' && params.error === 'access_denied') {
					swal({
						title: 'Authentication error.',
						text: 'Something went wrong with your integration account authentication. Please email contact@hackajob.co and report the issue. Thanks and sorry for the inconvenience!',
						type: 'error',
						showCancelButton: false,
						confirmButtonColor: '#008fb3',
						confirmButtonText: 'Ok',
						allowOutsideClick: false,
						allowEscapeKey: false
					}).then(() => {
						this.router.navigate(['/settings'], { queryParams: {}});
					});
				}

				if (typeof params.error !== 'undefined' && params.error === 'google_meet_disabled') {
					swal({
						title: 'Authentication error.',
						text: 'Google Meet is not active on your google account. Please connect your G Suite account or contact your System Administrator.',
						type: 'error',
						showCancelButton: false,
						confirmButtonColor: '#008fb3',
						confirmButtonText: 'Ok',
						allowOutsideClick: false,
						allowEscapeKey: false
					}).then(() => {
						this.router.navigate(['/settings'], { queryParams: {}});
					});
				}

				if (typeof params.action !== 'undefined' && params.action === 'config_availability_periods') {
					window.location.href = this.easyScheduleSettingsPage;
				}

				if (typeof params.error !== 'undefined' && typeof params.error_message !== 'undefined') {
					swal({
						title: 'Authentication error.',
						text: params.error_message,
						type: 'error',
						showCancelButton: false,
						confirmButtonColor: '#008fb3',
						confirmButtonText: 'Ok',
						allowOutsideClick: false,
						allowEscapeKey: false
					}).then(() => {
						this.router.navigate(['/settings'], { queryParams: {}});
					});
				}
			}
		);

		this.getClient();
		this.getThirdPartyIntegrations();
		this.getClientOptions();
	}

	public ngOnDestroy(): void {
		clearInterval(this.refreshWebhooksStatusIntervalIdx);
	}

	// Events
	public setAppTitle(): void  {
		this.titleService.setTitle('hackajob.co - Settings');
	}

	public onAccountSettingsSubmit(): void {
		let observableBatch = [];
		this.state.account.loading = true;

		if (this.accountSettings['interview-multiple-slots']) {
			let data = this.accountSettings['interview-multiple-slots'].model;
			observableBatch.push(this.sharedClientService.putAccountOptions(String(this.authService.userId), data));
		}

		if (this.client.is_master_account && this.accountSettings['interview-stage-application-enabled']) {
			let data = this.accountSettings['interview-stage-application-enabled'].model;
			observableBatch.push(this.sharedClientService.putAccountOptions(String(this.authService.userId), data));
		}

		if (this.clientOptions['phone-interview-medium']) {
			let data = [{
				option_identifier: 'phone-interview-medium',
				value_identifiers: [this.clientOptions['phone-interview-medium'].radio_value.identifier]
			}];
			observableBatch.push(this.sharedClientService.putAccountOptions(String(this.authService.userId), data));
		}

		if (this.clientOptions['face-to-face-interview-medium']) {
			let data = [{
				option_identifier: 'face-to-face-interview-medium',
				value_identifiers: [this.clientOptions['face-to-face-interview-medium'].radio_value.identifier]
			}];
			observableBatch.push(this.sharedClientService.putAccountOptions(String(this.authService.userId), data));
		}

		let clientData = {timezone: this.client.timezone};
		observableBatch.push(this.sharedClientService.postClient(String(this.authService.userId), clientData));

		Observable.forkJoin(observableBatch).subscribe((response) => {
			this.getAccountSettings(true);

			if (this.timeService.appTimezone !== this.client.timezone) {
				// reload app
				window.location.reload();
				return;
			}
		});
	}

	public onSecuritySubmit(): void {
		this.state.security.loading = true;
		let data = {
			value: {new: this.securitySettings.password}
		};
		this.sharedClientService.patchClientPassword(String(this.authService.userId), data).subscribe(
			(resp) => {
				this.state.security.loading = false;
				this.state.security.done = true;
				window.location.assign(process.env.LANDING_PAGES_URL);
			}
		);
	}

	public onMfaSubmit(): void {
		this.state.mfa.loading = true;
		let data = {
			password: this.mfaPasswordConfirmation,
			value: +this.client.mfa_phone_activated
		};
		this.sharedClientService.patchMfaPhoneActivation(String(this.authService.userId), data).subscribe(
			(resp) => {
				this.mfaPasswordConfirmationErrorMessage = '';
				this.mfaPasswordConfirmation = '';
				this.state.mfa.loading = false;
				this.state.mfa.done = true;
				setTimeout(() => {
					this.state.mfa.done = false;
				}, 2000);
			},
			(err) => {
				if (err.status === 400) {
					this. mfaPasswordConfirmationErrorMessage = 'Phone number is missing!';
				}
				if (err.status === 401) {
					this.mfaPasswordConfirmationErrorMessage = 'Password doesn\'t match!';
				}
				this.mfaPasswordConfirmation = '';
				this.state.mfa.loading = false;
				this.state.mfa.done = false;
			}
		);
	}

	public onRadioMfaPreferenceChange(): void {
		this.showConfirmYourPasswordInput = true;
		this.mfaPasswordConfirmation = '';
		this.mfaPasswordConfirmationErrorMessage = '';
	}

	public onConnectClick(integration: any): void {
		const integratedAts = this.atsIntegrations.find((integr) => integr.connected);
		if ((integration.category === 'ats' || integration.v3AtsIntegration) && integratedAts) {
			if (integratedAts.identifier !== integration.identifier && (integratedAts.identifier !== 'harbour' && integration.identifier !== 'harbour')) {
				swal({
					title: 'Connection error.',
					text: `You can't have multiple ATS connections at the same time!`,
					type: 'error',
					showCancelButton: false,
					confirmButtonColor: '#008fb3',
					confirmButtonText: 'Ok',
					allowOutsideClick: false,
					allowEscapeKey: false
				}).then();
				return;
			}
		}

		if (!integration.first_time_connection) {
			this.onAtsAdminRightsConfirmation(integration);
		} else {
			this.modal.alert()
				.showClose(true)
				.body('Please make sure you have Admin rights on the ATS account in order to avoid ATS connection failure.')
				.open()
				.then((closedModal) => closedModal.result.then(
					() => this.onAtsAdminRightsConfirmation(integration),
					() => undefined)
				);
		}
	}

	public redirectToSource(): void {
		if (this.authService.clientPermissions.hasPermission('sapience-mvp')) {
			window.location.assign(process.env.CLIENT_APP_URL + '/find-talent');
			return;
		}
		window.location.assign(process.env.CLIENT_APP_URL + '/source');
	}

	public onAtsExportSubmit(): void {
		if (this.clientOptions['ats-export-candidate']) {
			this.state.atsCandidateExport.loading = true;
			let clientOptions = [];
			clientOptions.push({
				option_identifier: 'ats-export-candidate',
				value_identifiers: [this.clientOptions['ats-export-candidate'].radio_value.identifier]
			});

			this.sharedClientService.putAccountOptions(String(this.authService.userId), clientOptions).subscribe(
				(response) => {
					this.state.atsCandidateExport.loading = false;
					return;
				}
			);
		}
	}

	public hasConferencesAndCalendarsIntegrationActive(identifier) {
		if (identifier === 'none')  {
			return true;
		}

		let thirdPartyIntegrationIdentifier = '';
		// convert option identifier to third party integration identifier
		switch (identifier) {
			case 'google-meet': {
				thirdPartyIntegrationIdentifier = 'google_meet';
				break;
			}
			case 'zoom': {
				thirdPartyIntegrationIdentifier = 'zoom';
				break;
			}
			case 'microsoft_teams': {
				thirdPartyIntegrationIdentifier = 'microsoft_teams';
				break;
			}
			case 'calendly': {
				thirdPartyIntegrationIdentifier = 'calendly';
				break;
			}
			default: {
				return false;
			}
		}

		const integration = this.conferencesAndCalendarsIntegrations.find((i) => i.identifier === thirdPartyIntegrationIdentifier);
		if (!integration) {
			return false;
		}

		return integration.connected;
	}

	public disconnectIntegration(integration) {
		if (integration.v3AtsIntegration) {
			this.sharedThirdPartyIntegrationService.deleteAtsIntegrationsV3().subscribe(
				() => this.getThirdPartyIntegrations()
			);
			return;
		}
		this.sharedThirdPartyIntegrationService.deleteEntity(String(this.authService.userId), integration.identifier).subscribe(
			() => this.getThirdPartyIntegrations()
		);
	}

	// Service calls
	private getClient(): void {
		this.sharedClientService.get(this.authService.userId).subscribe(
			(client) => {
				this.client = client;
				this.getAccountSettings();

				if (this.authService.clientPermissions.hasPermission('managing-application-tracking-systems')) {
					this.initWebhooksRefresher();
				}
			}
		);
	}

	private onAtsAdminRightsConfirmation(integration: any): void {
		if (integration.authentication_oauth_url) {
			window.location.href = integration.authentication_oauth_url;
			return;
		}
		let body = {};
		switch (integration.authentication_type) {
			case CLIENT_THIRD_PARTY_INTEGRATION_API_KEY_TYPE:
				integration.connectingError = null;

				if (!integration.providedApiKey) {
					return;
				}
				if (integration.authentication_config_additional_fields) {
					for (let idx in integration.authentication_config_additional_fields) {
						if (integration.authentication_config_additional_fields.hasOwnProperty(idx) && !integration.providedAdditionalFields[integration.authentication_config_additional_fields[idx].identifier]) {
							integration.connectingError = integration.authentication_config_additional_fields[idx].name + ' is required.';

							return;
						}
					}
				}

				integration.isConnecting = true;

				body['api_key'] = integration.providedApiKey;
				for (let idx in integration.authentication_config_additional_fields) {
					if (integration.authentication_config_additional_fields.hasOwnProperty(idx)) {
						body[integration.authentication_config_additional_fields[idx].identifier] = integration.providedAdditionalFields[integration.authentication_config_additional_fields[idx].identifier];
					}
				}

				this.sharedThirdPartyIntegrationService.postAccessCredentials(String(this.authService.userId), integration.identifier, body).subscribe(
					(resp) => {
						this.getThirdPartyIntegrations();
					},
					(err) => {
						integration.isConnecting = false;
						if (err === 401) {
							integration.connectingError = 'Invalid API Key.';
						}
						if (err === 404) {
							integration.connectingError = 'We were unable to find your email in the ' + integration.name + ' database.';
						}
						if (err === 400) {
							integration.connectingError = 'Your data provided is invalid.';
						}
					}
				);

				break;

			case CLIENT_THIRD_PARTY_INTEGRATION_BASIC_TYPE:
				body = {
					identifier: integration.identifier,
					connection_data: {
						username: integration.providedApiUsername,
						password: integration.providedApiPassword,
						domain: integration.providedApiDomain
					}
				};
				this.sharedThirdPartyIntegrationService.connectAtsIntegrationV3(body).subscribe(
					() => {
						this.getThirdPartyIntegrations();
					},
					() => {
						integration.isConnecting = false;
						integration.connectingError = 'Something went wrong with your integration account authentication.';
					}
				);

				break;

			case CLIENT_THIRD_PARTY_INTEGRATION_KOMBO_TYPE:
				this.sharedThirdPartyIntegrationService.initAtsIntegrationKomboV3(integration.identifier).subscribe(
					(initResp) => {
						let link = initResp.link;
						showKomboConnect(link).then(
							(activationToken) => {
								let activationBody = {
									token: activationToken
								};
								this.sharedThirdPartyIntegrationService.activateAtsIntegrationKomboV3(integration.identifier, activationBody).subscribe(
									() => {
										this.getThirdPartyIntegrations();
									},
									() => {
										integration.isConnecting = false;
										integration.connectingError = 'Something went wrong with your integration account authentication.';
									}
								);
							}
						).catch(() => {
							integration.isConnecting = false;
							integration.connectingError = 'Something went wrong with your integration account authentication.';
						});
					},
					() => {
						integration.isConnecting = false;
						integration.connectingError = 'Something went wrong with your integration account authentication.';
					}
				);

				break;

			default:
				break;
		}
	}

	private getThirdPartyIntegrations(): void {
		Observable.forkJoin([
			this.sharedThirdPartyIntegrationService.get(String(this.authService.userId)),
			this.sharedThirdPartyIntegrationService.getAtsIntegrationsV3()
		]).subscribe(
			([v1Integrations, v3Integrations]) => {
				for (let idx in v1Integrations) {
					if (v1Integrations.hasOwnProperty(idx) && v1Integrations[idx]['authentication_config'] && v1Integrations[idx]['authentication_config']['additional_fields']) {
						v1Integrations[idx]['providedAdditionalFields'] = [];
					}
				}

				this.atsIntegrations = [];

				if (v3Integrations && v3Integrations.length) {
					v3Integrations.forEach((integ) => {
						this.atsIntegrations.push({
							...integ,
							v3AtsIntegration: true
						});
					});
				}

				const mappedV1Integrations = v1Integrations.map((integ) => {
					return {
						...integ,
						logo: integ.authentication_config.logo,
						background_url: integ.authentication_config.background,
						short_description: integ.authentication_config.short_description,
						documentation_url: integ.authentication_config.documentation_url,
						authentication_config_additional_fields: integ.authentication_config.additional_fields,
						authentication_oauth_url: integ.authentication_config.oauth_url,
						connected: integ.is_user_authenticated,
						first_time_connection: integ.is_first_time_connection,
						is_user_allowed_to_connect: this.authService.clientPermissions.hasPermission('managing-application-tracking-systems') || integ.authentication_config.only_authorized_third_party_integrations_client === false
					};
				});

				mappedV1Integrations.filter((integration) => integration.category === 'ats').forEach((integr) => {
					this.atsIntegrations.push(integr);
				});

				this.conferencesAndCalendarsIntegrations = mappedV1Integrations.filter((integration) => integration.category !== 'ats');
			}
		);
	}

	private getAccountSettings(changes?: boolean): void {
		this.sharedClientService.getAccountOptions(String(this.authService.userId)).subscribe(
			(settings) => {
				if (settings.hasOwnProperty('interview-multiple-slots')) {
					settings['interview-multiple-slots']['model'] = [{
						option_identifier: settings['interview-multiple-slots'].option.identifier,
						value_identifiers: [settings['interview-multiple-slots'].radio_value.identifier]
					}];
					this.accountSettings = settings;
				}
				if (this.client.is_master_account && settings.hasOwnProperty('interview-stage-application-enabled')) {
					settings['interview-stage-application-enabled']['model'] = [{
						option_identifier: settings['interview-stage-application-enabled'].option.identifier,
						value_identifiers: [settings['interview-stage-application-enabled'].radio_value.identifier]
					}];
					this.accountSettings = settings;
				}
				this.state.account.loading = false;
				if (changes) {
					this.state.account.done = true;
					setTimeout(() => {
						this.state.account.done = false;
					}, 2000);
				}
			}
		);
	}

	private initWebhooksRefresher(): any {
		// refresh the webhooks statuses
		this.refreshWebhooksStatusIntervalIdx = setInterval(() => {
			if (this.atsIntegrations && this.atsIntegrations.length) {
				this.sharedThirdPartyIntegrationService.get(String(this.authService.userId)).subscribe(
					(services) => {
						for (let idx in services) {
							if (services.hasOwnProperty(idx) && services[idx]['authentication_data'] && services[idx]['authentication_data']['webhooks_status']) {
								const integrationIdx = this.atsIntegrations.findIndex((interg) => interg.identifier === services[idx].identifier);
								if (integrationIdx > -1) {
									this.atsIntegrations[integrationIdx]['authentication_data'] = services[idx]['authentication_data'];
								}
							}
						}
					}
				);
			}
		}, 10000);
	}

	private getClientOptions() {
		return this.sharedClientService.getClientOptions(this.authService.userId.toString()).subscribe(
			(clientOptions) => {
				this.clientOptions = clientOptions;
			}
		);
	}
static ctorParameters() { return [ { type: Modal }, { type: AuthService }, { type: ActivatedRoute }, { type: Router }, { type: Title }, { type: AuthHttpClient }, { type: SharedClientService }, { type: SharedThirdPartyIntegrationService }, { type: TimeService } ]; }}



// WEBPACK FOOTER //
// ./node_modules/ngc-webpack??ref--0-2!./node_modules/angular2-template-loader!./src/app/components/client/settings/settings.component.ts